import { createPersistentEmitter } from 'src/lib/Emitter';
import { PopupID } from '../components/popups/popupRegistry';

export type Handler = (opts: any) => void;
export type Handlers = { [id: string]: Handler[] };

class PopupMonitor {
  private openPopups: Partial<Record<PopupID, boolean>> = {};
  private onOpenHandlers: Handlers = {};
  private onCloseHandlers: Handlers = {};

  public init() {
    createPersistentEmitter((state) => state.ui.togglePopup).addListener(
      (command) => {
        if (!command || command.enabled === this.openPopups[command.id]) {
          return;
        }

        this.openPopups[command.id] = command.enabled;

        const handlers = command.enabled
          ? this.onOpenHandlers
          : this.onCloseHandlers;
        this.callHandlers(handlers, command.id, command.opts);
      },
    );
  }

  /**
   * Checks if any popup is currently open.
   *
   * @returns {boolean} `true` if any popup is open, otherwise `false`.
   */
  public isAnyPopupOpen(): boolean {
    return Object.values(this.openPopups).some((isOpen) => isOpen);
  }

  public isOpen(id: PopupID) {
    return !!this.openPopups[id];
  }

  public getOpenPopups(): PopupID[] {
    return Object.keys(this.openPopups).filter((id) =>
      this.isOpen(id as PopupID),
    ) as PopupID[];
  }

  public addOnOpenHandler(id: string, handler: Handler) {
    return this.addHandler(this.onOpenHandlers, id, handler);
  }

  public removeOnOpenHandler(id: string, handler: Handler) {
    return this.removeHandler(this.onOpenHandlers, id, handler);
  }

  public addOnCloseHandler(id: string, handler: Handler) {
    return this.addHandler(this.onCloseHandlers, id, handler);
  }

  public removeOnCloseHandler(id: string, handler: Handler) {
    return this.removeHandler(this.onCloseHandlers, id, handler);
  }

  private addHandler(handlers: Handlers, id: string, handler: Handler) {
    if (!handlers[id]) {
      handlers[id] = [];
    }

    handlers[id].push(handler);

    return handler;
  }

  private removeHandler(handlers: Handlers, id: string, handler: Handler) {
    const index = handlers[id].indexOf(handler);
    if (index < 0) {
      throw new Error('Handler not found.');
    }

    handlers[id].splice(index, 1);
  }

  private callHandlers(handlers: Handlers, id: string, opts: any) {
    if (!handlers[id]) {
      return;
    }

    handlers[id].forEach((handler) => handler(opts));
  }
}

export default new PopupMonitor();
